/**
 * AI Work Study - Main Application Component
 * Manages survey flow and screen transitions
 * 
 * Supports multiple modes:
 * 1. Token-based (from admin link): /survey/{token} - Employee confirms pre-filled details
 * 2. Admin mode: / or /admin - People Operations dashboard (requires Azure Entra ID login)
 * 
 * Default page is admin login - employees only access via direct survey links
 */

import React, { useState, useCallback, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import WelcomeScreen from './components/WelcomeScreen';
import SurveyChat from './components/SurveyChat';
import CompletionScreen from './components/CompletionScreen';
import DocumentSidebar from './components/DocumentSidebar';
import AdminDashboard from './components/AdminDashboard';
import AdminLogin from './components/AdminLogin';
import AdminWelcome from './components/AdminWelcome';
import EmployeeReportPage from './components/EmployeeReportPage';
import PulseSurveyPage from './components/PulseSurveyPage';
import LeadershipViewPage from './components/LeadershipViewPage';
import ActionPlanViewPage from './components/ActionPlanViewPage';
import DepartmentSummaryPage from './components/DepartmentSummaryPage';
import { surveyAPI, confirmDetails, API_BASE_URL } from './services/api';
import './styles/survey.css';
import './styles/admin.css';

// Screen states
const SCREENS = {
    LOADING: 'loading',
    WELCOME: 'welcome',
    SURVEY: 'survey',
    COMPLETE: 'complete',
    ERROR: 'error',
    ADMIN_LOGIN: 'admin_login',
    ADMIN: 'admin',
    ADMIN_WELCOME: 'admin_welcome',
    UNAUTHORIZED: 'unauthorized',
    EMPLOYEE_REPORT: 'employee_report',
    PULSE_SURVEY: 'pulse_survey',
    LEADERSHIP_VIEW: 'leadership_view',
    ACTION_PLAN_VIEW: 'action_plan_view',
    DEPARTMENT_SUMMARY_VIEW: 'department_summary_view',
};

/**
 * Extract token from URL path
 * Supports: /survey/{token} or ?token={token}
 */
function getTokenFromURL() {
    // Check path: /survey/{token}
    const pathMatch = window.location.pathname.match(/\/survey\/([^/]+)/);
    if (pathMatch) {
        return pathMatch[1];
    }
    
    // Check query string: ?token={token}
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
}

/**
 * Check if we're on the admin page
 */
function isAdminPage() {
    return window.location.pathname === '/' || window.location.pathname.startsWith('/admin');
}

/**
 * Check if we're on the employee report page
 */
function getEmployeeReportToken() {
    const match = window.location.pathname.match(/\/employee\/report\/([^/]+)/);
    return match ? match[1] : null;
}

/**
 * Check if we're on the pulse survey page
 */
function getPulseSurveyToken() {
    const match = window.location.pathname.match(/\/pulse\/([^/]+)/);
    return match ? match[1] : null;
}

function getLeadershipViewToken() {
    const match = window.location.pathname.match(/\/leadership\/([^/]+)/);
    return match ? match[1] : null;
}

function getActionPlanViewToken() {
    const match = window.location.pathname.match(/\/action-plan\/([^/]+)/);
    return match ? match[1] : null;
}

function getDepartmentSummaryViewToken() {
    const match = window.location.pathname.match(/\/dept-summary\/([^/]+)/);
    return match ? match[1] : null;
}

/**
 * Check if we're on the admin welcome page (post-invite landing)
 */
function isAdminWelcomePage() {
    return window.location.pathname === '/admin/welcome';
}

/**
 * Check if we're on the unauthorized page
 */
function isUnauthorizedPage() {
    return window.location.pathname.startsWith('/unauthorized');
}

/**
 * Main App Content - separated to use auth hook inside AuthProvider
 */
function AppContent() {
    const { isAuthenticated, isAdmin, loading: authLoading, user, logout } = useAuth();
    // Application state
    const [currentScreen, setCurrentScreen] = useState(SCREENS.LOADING);
    const [surveyToken, setSurveyToken] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [prefilledData, setPrefilledData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [adminUser, setAdminUser] = useState(null);
    const [publicToken, setPublicToken] = useState(null);

    /**
     * Loads a pre-filled survey from admin-created token
     */
    const loadPrefilledSurvey = useCallback(async (token) => {
        try {
            setIsLoading(true);
            const status = await surveyAPI.getSurveyStatus(token);
            
            if (status.status === 'completed') {
                // Survey already completed
                setSurveyToken(token);
                setSessionData(status);
                setCurrentScreen(SCREENS.COMPLETE);
            } else if (status.details_confirmed) {
                // Already confirmed, continue survey
                setSurveyToken(token);
                setSessionData(status);
                localStorage.setItem('survey_token', token);
                localStorage.setItem('survey_session', JSON.stringify(status));
                setCurrentScreen(SCREENS.SURVEY);
            } else {
                // Show welcome screen with pre-filled data for confirmation
                setSurveyToken(token);
                setPrefilledData(status);
                setCurrentScreen(SCREENS.WELCOME);
            }
        } catch (err) {
            // Invalid token
            setError('This survey link is invalid or has expired. Please contact People Operations for a new link.');
            setCurrentScreen(SCREENS.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Initialize the app - check for token or Azure auth session
     */
    const initializeApp = useCallback(async () => {
        // Wait for auth check to complete
        if (authLoading) {
            setCurrentScreen(SCREENS.LOADING);
            return;
        }
        
        // Check for survey token in URL first - employee access
        const urlToken = getTokenFromURL();
        const employeeReportToken = getEmployeeReportToken();
        const pulseToken = getPulseSurveyToken();
        const leadershipToken = getLeadershipViewToken();
        const actionPlanToken = getActionPlanViewToken();
        const departmentSummaryToken = getDepartmentSummaryViewToken();

        if (employeeReportToken) {
            // Public employee report page — no auth required
            setPublicToken(employeeReportToken);
            setCurrentScreen(SCREENS.EMPLOYEE_REPORT);
        } else if (pulseToken) {
            // Public pulse survey page — no auth required
            setPublicToken(pulseToken);
            setCurrentScreen(SCREENS.PULSE_SURVEY);
        } else if (leadershipToken) {
            // Public leadership department view — no auth required
            setPublicToken(leadershipToken);
            setCurrentScreen(SCREENS.LEADERSHIP_VIEW);
        } else if (actionPlanToken) {
            // Public department action plan view — no auth required
            setPublicToken(actionPlanToken);
            setCurrentScreen(SCREENS.ACTION_PLAN_VIEW);
        } else if (departmentSummaryToken) {
            // Public employee-facing department summary view — no auth required
            setPublicToken(departmentSummaryToken);
            setCurrentScreen(SCREENS.DEPARTMENT_SUMMARY_VIEW);
        } else if (isUnauthorizedPage()) {
            // If the platform rewrote to /unauthorized during login, re-evaluate auth before showing denial
            if (authLoading) {
                setCurrentScreen(SCREENS.LOADING);
            } else if (isAdmin && user) {
                setAdminUser({
                    username: user.userDetails || user.userId,
                    email: user.userDetails,
                    is_primary_admin: true,
                    roles: user.userRoles,
                });
                // If we actually are admin, restore the admin screen instead of staying on /unauthorized
                window.history.replaceState({}, '', '/admin');
                setCurrentScreen(SCREENS.ADMIN);
            } else if (!isAuthenticated) {
                // Not logged in yet—send to login instead of showing unauthorized
                window.history.replaceState({}, '', '/admin/login');
                setCurrentScreen(SCREENS.ADMIN_LOGIN);
            } else {
                // Authenticated but missing admin role
                setCurrentScreen(SCREENS.UNAUTHORIZED);
            }
        } else if (isAdminWelcomePage()) {
            // Post-invite welcome page - only requires 'authenticated' role
            // The AdminWelcome component handles checking for admin role and redirecting
            setCurrentScreen(SCREENS.ADMIN_WELCOME);
        } else if (urlToken) {
            // Token found in URL - load pre-filled survey
            await loadPrefilledSurvey(urlToken);
        } else if (isAdminPage()) {
            // Admin page - require Azure SWA role assignment
            if (isAdmin && user) {
                setAdminUser({
                    username: user.userDetails || user.userId,
                    email: user.userDetails,
                    is_primary_admin: true,
                    roles: user.userRoles,
                });
                setCurrentScreen(SCREENS.ADMIN);
            } else if (isAuthenticated) {
                // Authenticated but missing admin role
                setCurrentScreen(SCREENS.UNAUTHORIZED);
            } else {
                setCurrentScreen(SCREENS.ADMIN_LOGIN);
            }
        } else {
            // No token, no admin page - redirect to admin login
            setCurrentScreen(SCREENS.ADMIN_LOGIN);
        }
    }, [authLoading, isAuthenticated, isAdmin, user, loadPrefilledSurvey]);

    // Check for token in URL or existing session on mount
    useEffect(() => {
        initializeApp();
    }, [initializeApp]);

    /**
     * Attempts to resume an existing survey session
     */
    const resumeSession = async (token, session) => {
        try {
            setIsLoading(true);
            const status = await surveyAPI.getSurveyStatus(token);
            
            if (status.status === 'completed') {
                // Survey already completed
                setSessionData(status);
                setCurrentScreen(SCREENS.COMPLETE);
            } else {
                // Resume in-progress survey
                setSurveyToken(token);
                setSessionData(status);
                setCurrentScreen(SCREENS.SURVEY);
            }
        } catch (err) {
            // Session invalid or expired, clear and start fresh
            localStorage.removeItem('survey_token');
            localStorage.removeItem('survey_session');
            console.log('Previous session expired, starting fresh');
            setCurrentScreen(SCREENS.WELCOME);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handles confirming pre-filled details (admin-created survey)
     */
    const handleConfirmDetails = useCallback(async () => {
        setError(null);
        setIsLoading(true);

        try {
            // Confirm the pre-filled details
            await confirmDetails(surveyToken);
            
            // Reload status to get session data
            const status = await surveyAPI.getSurveyStatus(surveyToken);
            setSessionData(status);

            // Save to localStorage for session persistence
            localStorage.setItem('survey_token', surveyToken);
            localStorage.setItem('survey_session', JSON.stringify(status));

            // Transition to survey screen
            setCurrentScreen(SCREENS.SURVEY);
        } catch (err) {
            setError(err.message || 'Failed to start survey. Please try again.');
            console.error('Error confirming details:', err);
        } finally {
            setIsLoading(false);
        }
    }, [surveyToken]);

    /**
     * Handles starting a new survey (manual mode)
     * @param {Object} employeeData - Employee information from welcome form
     */
    const handleStartSurvey = useCallback(async (employeeData) => {
        setError(null);
        setIsLoading(true);

        try {
            // Create new survey session via API
            const session = await surveyAPI.startSurvey({
                employee_name: employeeData.name,
                role_title: employeeData.role,
                department: employeeData.department,
                job_description: employeeData.jobDescription || null,
            });

            // Store session info
            setSurveyToken(session.token);
            setSessionData(session);

            // Save to localStorage for session persistence
            localStorage.setItem('survey_token', session.token);
            localStorage.setItem('survey_session', JSON.stringify(session));

            // Transition to survey screen
            setCurrentScreen(SCREENS.SURVEY);
        } catch (err) {
            setError(err.message || 'Failed to start survey. Please try again.');
            console.error('Error starting survey:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Handles starting a new survey with file upload
     * @param {Object} employeeData - Employee information from welcome form
     * @param {File} jobDescriptionFile - Job description file
     */
    const handleStartSurveyWithFile = useCallback(async (employeeData, jobDescriptionFile) => {
        setError(null);
        setIsLoading(true);

        try {
            // Create new survey session with file via API
            const session = await surveyAPI.startSurveyWithFile({
                employee_name: employeeData.name,
                role_title: employeeData.role,
                department: employeeData.department,
            }, jobDescriptionFile);

            // Store session info
            setSurveyToken(session.token);
            setSessionData(session);

            // Save to localStorage for session persistence
            localStorage.setItem('survey_token', session.token);
            localStorage.setItem('survey_session', JSON.stringify(session));

            // Transition to survey screen
            setCurrentScreen(SCREENS.SURVEY);
        } catch (err) {
            setError(err.message || 'Failed to start survey. Please try again.');
            console.error('Error starting survey with file:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Handles survey completion
     */
    const handleSurveyComplete = useCallback(() => {
        // Clear session from localStorage
        localStorage.removeItem('survey_token');
        localStorage.removeItem('survey_session');
        
        // Transition to completion screen
        setCurrentScreen(SCREENS.COMPLETE);
    }, []);

    /**
     * Handles starting a new survey from completion screen
     */
    const handleStartNew = useCallback(() => {
        setSurveyToken(null);
        setSessionData(null);
        setPrefilledData(null);
        setError(null);
        // Clear URL token if present, go to admin login
        window.history.pushState({}, '', '/');
        setCurrentScreen(SCREENS.ADMIN_LOGIN);
    }, []);

    /**
     * Handles admin logout - uses Azure SWA logout
     */
    const handleAdminLogout = useCallback(async () => {
        setAdminUser(null);
        logout('/admin/login');
    }, [logout]);



    // Loading state
    if (currentScreen === SCREENS.LOADING) {
        return (
            <div className="app-container">
                <div className="loading-screen">
                    <div className="loading-spinner"></div>
                    <p>Loading your survey...</p>
                </div>
            </div>
        );
    }

    // Error state (invalid token)
    if (currentScreen === SCREENS.ERROR) {
        return (
            <div className="app-container">
                <div className="error-screen">
                    <div className="error-content">
                        <span className="error-icon">⚠️</span>
                        <h1>Survey Not Found</h1>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render appropriate screen
    return (
        <div className="app-container">
            {error && (
                <div className="error-banner">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {currentScreen === SCREENS.WELCOME && (
                <WelcomeScreen 
                    onStart={handleStartSurvey}
                    onStartWithFile={handleStartSurveyWithFile}
                    onConfirm={handleConfirmDetails}
                    isLoading={isLoading}
                    prefilledData={prefilledData}
                />
            )}

            {currentScreen === SCREENS.SURVEY && surveyToken && (
                <>
                    <SurveyChat 
                        token={surveyToken} 
                        sessionData={sessionData}
                        onComplete={handleSurveyComplete}
                    />
                    <DocumentSidebar token={surveyToken} />
                </>
            )}

            {currentScreen === SCREENS.COMPLETE && (
                <CompletionScreen 
                    employeeName={sessionData?.employee_name}
                />
            )}

            {currentScreen === SCREENS.UNAUTHORIZED && (
                <div className="app-container">
                    <div className="error-screen">
                        <div className="error-content">
                            <span className="error-icon">🚫</span>
                            <h1>Access Denied</h1>
                            <p>Your account is authenticated but is not authorized to access the People Ops portal. If you need access, request the Static Web App admin role assignment.</p>
                            <button className="admin-login-button" onClick={() => logout('/admin/login')}>
                                Switch Account
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {currentScreen === SCREENS.ADMIN_WELCOME && (
                <AdminWelcome />
            )}

            {currentScreen === SCREENS.ADMIN_LOGIN && (
                <AdminLogin />
            )}

            {currentScreen === SCREENS.ADMIN && (
                <AdminDashboard 
                    adminUser={adminUser}
                    onLogout={handleAdminLogout}
                />
            )}

            {currentScreen === SCREENS.EMPLOYEE_REPORT && publicToken && (
                <EmployeeReportPage token={publicToken} />
            )}

            {currentScreen === SCREENS.PULSE_SURVEY && publicToken && (
                <PulseSurveyPage token={publicToken} />
            )}

            {currentScreen === SCREENS.LEADERSHIP_VIEW && publicToken && (
                <LeadershipViewPage token={publicToken} />
            )}

            {currentScreen === SCREENS.ACTION_PLAN_VIEW && publicToken && (
                <ActionPlanViewPage token={publicToken} />
            )}

            {currentScreen === SCREENS.DEPARTMENT_SUMMARY_VIEW && publicToken && (
                <DepartmentSummaryPage token={publicToken} />
            )}


        </div>
    );
}

/**
 * Root App component wrapped with AuthProvider
 */
function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
